/**
 * Normalise a v2 (Hegic) `GET /assessment` response into the shape CaseDetails
 * already consumes, so the page renders without a per-client branch in the view.
 *
 * The two contracts differ structurally, not cosmetically — see HEGIC_API.md:
 *
 *   legacy (Bajaj/Ergo)          v2 (Hegic)
 *   ------------------------     --------------------------------------------
 *   info                         claim_summary  (counts only, no vehicle data)
 *   summary                      derived from assess + parts
 *   assessment_details[]         assess + parts + partdamages, joined on part_id
 *   all_damages[]                damages   (dict keyed by damage id)
 *   uploads[]                    carImages (dict keyed by image id)
 *   arrays                       dicts keyed by id, everywhere
 *
 * Anything v2 genuinely does not carry is left undefined rather than faked, so
 * the gaps stay visible instead of rendering as confident zeroes.
 */

// Every v2 collection is an object keyed by id, never an array.
const values = (dict) => (dict && typeof dict === "object" ? Object.values(dict) : []);

/**
 * v2 is inconsistent with itself: `parts` uses snake_case sides (front_right)
 * while `damages` and `carImages` use camelCase (frontRight). Normalise to the
 * snake_case form the rest of the app matches on.
 */
export const normalizeSide = (side) =>
  typeof side === "string"
    ? side.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
    : "";

/**
 * ── Hegic's damage scale ──────────────────────────────────────────────────
 *
 * The API reports intensity 0-10, but nobody reads it that way. The portal
 * rebuckets it to 1-5 before showing or storing anything, and every figure a
 * Hegic user has ever seen or typed is on that 1-5 scale — its review screen
 * clamps its own input to it. Reporting the raw 0-10 here made our screen
 * disagree with theirs about the same part: raw 7 is "Replace" to them and was
 * "moderate" to us.
 *
 * Buckets, verbatim from the portal's review page:
 *
 *   API 0    -> 1  No Damage
 *   API 1-4  -> 2  Scratch
 *   API 5-6  -> 3  Minor dent
 *   API 7-8  -> 4  Heavy dent
 *   API 9-10 -> 5  Tear
 */
export const toPortalIntensity = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  if (n >= 9) return 5;
  if (n >= 7) return 4;
  if (n >= 5) return 3;
  if (n >= 1) return 2;
  return 1;
};

export const PORTAL_SCALE_MAX = 5;

export const INTENSITY_LABELS = {
  1: "No Damage",
  2: "Scratch",
  3: "Minor dent",
  4: "Heavy dent",
  5: "Tear",
};

/** The portal's own rule: above 3 is a replacement, 2-3 a repair, 1 is clear. */
export const actionFor = (intensity) => {
  const n = Number(intensity) || 0;
  if (n > 3) return "Replace";
  if (n > 1) return "Repair";
  return "No Damage";
};

/**
 * Severity bucket, on the 1-5 scale.
 *
 * Aligned to the action boundary rather than split evenly: severe means the
 * portal would say Replace. A colour that disagreed with the word next to it
 * would be worse than a coarse one.
 */
export const severityOf = (intensity) => {
  const n = Number(intensity) || 0;
  if (n >= 4) return "severe"; // Replace
  if (n === 3) return "moderate";
  if (n === 2) return "minor";
  return "none";
};

// A row is a real finding only if the AI did not clear it and it is not a
// flagged false positive.
//
// The portal keeps is_fp rows and shows them as damage. Deliberately not copied:
// is_fp means a human marked this a false positive, its own UI for doing so is
// commented out on that page, and presenting a known false positive as "Replace"
// states something nobody believes.
const isRealDamage = (row) =>
  row && row.action !== "No Damage" && !Number(row.is_fp);

/**
 * Which writers report on the raw 0-10 scale.
 *
 * A row written through the portal's review screen is already 1-5, so
 * rebucketing it again would drag every reviewer edit downwards. The portal
 * draws this line by user_type plus the two surveyor-app service accounts, and
 * rebuckets nothing else — copied exactly, because the point of the merge is
 * that the two screens agree.
 */
const SURVEYOR_APP_ACCOUNTS = new Set([
  "hdfc@hdfcergo.com",
  "hdfcinternal@hdfcergo.com",
]);

const writesRawScale = (row) => {
  const type = String(row?.user_type ?? "");
  if (type === "1" || type === "2" || type === "3") return true;
  return type === "0" && SURVEYOR_APP_ACCOUNTS.has(row?.user_id);
};

const clampToPortalScale = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(PORTAL_SCALE_MAX, Math.max(1, Math.round(n)));
};

/**
 * Vehicle fields, from the separate GET /vehicle call.
 *
 * v2 splits what the legacy contract returned as one payload: /assessment has the
 * damage, /vehicle has the vehicle and — importantly — the claim_state that
 * /assessment omits entirely.
 */
const vehicleInfo = (vehicle) => {
  if (!vehicle || typeof vehicle !== "object") return {};

  // The ANPR block carries the colour, and its own make/model guess. The
  // top-level make/model is the corrected value and wins; the guess can disagree
  // (Creta vs Alcazar in the sample) and is not surfaced as fact.
  const anpr = Array.isArray(vehicle.result) ? vehicle.result[0] : null;
  const colour = anpr?.color?.[0]?.color;

  return {
    claim_state: vehicle.claim_state,
    make: vehicle.make,
    model: vehicle.model,
    num_plate: vehicle.num_plate,
    odo_meter_reading: vehicle.odo_meter_reading,
    // The legacy shape calls this `vin`; v2 reports it as a reading, since it is
    // OCR'd from the plate/chassis photo rather than keyed in.
    vin: vehicle.vin_reading,
    vin_reading: vehicle.vin_reading,
    primary_color: colour,
    vehicle_class: vehicle.vehicle_class,
    vehicle_type: anpr?.vehicle?.type,
    engine_working: vehicle.engine_working,
    created_at: vehicle.created_at,
    updated_at: vehicle.updated_at,
    surveyor_name: vehicle.surveyor_name,
    user_id: vehicle.user_id,
    image_count: vehicle.image_count,
    re_assessment_count: vehicle.re_assessment_count,
    score: vehicle.score,
    comment: vehicle.comment,
    reviewer_comment: vehicle.reviewer_comment,
  };
};

export const toLegacyAssessment = (raw, { vehicle } = {}) => {
  if (!raw || typeof raw !== "object") return null;

  const claimSummary = raw.claim_summary || {};
  const assessRows = values(raw.assess);
  const parts = raw.parts || {};
  const partDamages = values(raw.partdamages);
  const damages = values(raw.damages);
  const carImages = values(raw.carImages);

  /**
   * ── The finding that stands for each panel ──────────────────────────────
   *
   * `assess` is the AI's output. `assess_history` is what happened to it since:
   * the surveyor app's edits, and the reviewer's. Reading only `assess` — which
   * is what this adapter used to do — showed the AI's original number even
   * after a human had overruled it, so our screen could contradict the portal
   * about a part a reviewer had already corrected.
   *
   * The portal's precedence, copied: the first history row for a panel wins,
   * and `assess` only fills in panels history never mentions. Keyed by panel
   * NAME, not part_id, because the API reports one panel over several rows
   * (one per camera angle) and a correction applies to the panel.
   *
   * "First" is the API's own ordering, which the portal depends on for this to
   * mean "most recent". Rows also carry a monotonic `id`; if the ordering ever
   * turns out to be oldest-first, sort on that here and the portal is the one
   * that needs fixing.
   */
  const nameOf = (partId) => parts[partId]?.full_name || "";

  const findingByName = new Map();

  values(raw.assess_history).forEach((row) => {
    const name = nameOf(row?.part_id);
    if (!name || findingByName.has(name)) return;
    findingByName.set(name, {
      partId: row.part_id,
      intensity: writesRawScale(row)
        ? toPortalIntensity(row.intensity)
        : clampToPortalScale(row.intensity),
      assessId: row.assess_id,
      partdamageId: row.partdamage_id,
      isFp: Boolean(Number(row.is_fp)),
      editedBy: row.user_id || null,
      editedByType: row.user_type ?? null,
      fromHistory: true,
    });
  });

  // Worst AI row per panel: `assess` carries one row per damage, so a panel
  // appears many times and its finding is the worst of them, not the first.
  const worstAssessByName = new Map();
  assessRows.filter(isRealDamage).forEach((row) => {
    const name = nameOf(row.part_id);
    if (!name) return;
    const current = worstAssessByName.get(name);
    if (!current || Number(row.intensity) > Number(current.intensity)) {
      worstAssessByName.set(name, row);
    }
  });

  worstAssessByName.forEach((row, name) => {
    if (findingByName.has(name)) return;
    findingByName.set(name, {
      partId: row.part_id,
      intensity: toPortalIntensity(row.intensity),
      assessId: row.id,
      partdamageId: row.partdamage_id,
      isFp: Boolean(Number(row.is_fp)),
      editedBy: null,
      editedByType: null,
      fromHistory: false,
    });
  });

  // Kept for the image indexes below, which are keyed by part_id.
  const worstByPart = new Map();
  [...findingByName.values()].forEach((finding) => {
    worstByPart.set(finding.partId, { intensity: finding.intensity });
  });

  const damageImagesByPart = partDamages.reduce((acc, pd) => {
    if (!acc[pd.part_id]) acc[pd.part_id] = [];
    acc[pd.part_id].push({
      id: pd.id,
      picture: pd.picture,
      side: normalizeSide(pd.side),
      // v2 joins the types of every coordinate into one string
      dam_type: pd.dam_type,
      damage_types: typeof pd.dam_type === "string" ? pd.dam_type.split(",") : [],
    });
    return acc;
  }, {});

  /**
   * Every crop the API holds for a panel, keyed by its name.
   *
   * `parts` carries one row per (panel, camera angle) pair: five rows named
   * "Right Head Lamp" are five photographs of the same lamp, not duplicates.
   * Only one of them usually has an assess row, so taking images from the
   * assessed row alone loses the other four — which is what the legacy portal
   * shows, and what a reviewer needs to judge a panel they cannot see from one
   * angle.
   *
   * The portal's own rule (its `all_images__` map): for each instance prefer
   * that instance's damage crop when the panel is damaged, else its plain part
   * crop. Damage is judged per panel, not per instance, because that is the
   * figure being reviewed.
   */
  const damageCropByPartId = new Map();
  partDamages.forEach((pd) => {
    if (pd?.part_id != null && pd.picture && !damageCropByPartId.has(pd.part_id)) {
      damageCropByPartId.set(pd.part_id, pd.picture);
    }
  });

  const worstIntensityByName = new Map();
  worstByPart.forEach((row, partId) => {
    const name = parts[partId]?.full_name;
    if (!name) return;
    const intensity = Number(row.intensity) || 0;
    if (intensity > (worstIntensityByName.get(name) || 0)) {
      worstIntensityByName.set(name, intensity);
    }
  });

  const imagesByName = new Map();
  values(parts).forEach((row) => {
    const name = row?.full_name;
    if (!name) return;
    const damaged = (worstIntensityByName.get(name) || 0) > 0;
    const picture =
      (damaged ? damageCropByPartId.get(row.id) : null) || row.picture;
    if (!picture) return;
    if (!imagesByName.has(name)) imagesByName.set(name, []);
    const list = imagesByName.get(name);
    if (!list.includes(picture)) list.push(picture);
  });

  const assessmentDetails = [...findingByName.entries()].map(
    ([name, finding]) => {
      const part = parts[finding.partId] || {};
      const severity = severityOf(finding.intensity);
      return {
        part_id: finding.partId,
        part_name: name,
        side: normalizeSide(part.side),
        picture: part.picture,
        // Recomputed from the merged intensity rather than taken from the row:
        // the portal does the same, and a stored action can predate the edit
        // that changed the intensity under it.
        action: actionFor(finding.intensity),
        Intensity: finding.intensity, // capital I: what the stencil code reads
        intensity: finding.intensity,
        intensity_label: INTENSITY_LABELS[finding.intensity] || "",
        // Scale is now 1-5, so anything rendering "x/10" needs telling.
        score_max: PORTAL_SCALE_MAX,
        // Set explicitly so buildDamageModel does not re-derive a bucket from a
        // 1-5 number with 0-10 thresholds.
        damage_severity: severity === "none" ? null : severity,
        assess_id: finding.assessId,
        partdamage_id: finding.partdamageId,
        is_fp: finding.isFp,
        // Who last touched it, so the view can say the AI has been overruled.
        edited_by: finding.editedBy,
        edited_by_type: finding.editedByType,
        from_history: finding.fromHistory,
        damage_images: damageImagesByPart[finding.partId] || [],
        // Every angle this panel was photographed from, not just the assessed one.
        part_images: imagesByName.get(name) || [],
      };
    },
  );

  const damagedDetails = assessmentDetails.filter(
    (part) => part.action !== "No Damage",
  );

  const buckets = damagedDetails.reduce(
    (acc, part) => {
      const bucket = severityOf(part.intensity);
      if (bucket !== "none") acc[bucket] += 1;
      return acc;
    },
    { severe: 0, moderate: 0, minor: 0 },
  );

  // vehicle_id is the id the list and PUT /vehicle/{id}/ use. It is NOT
  // claim_summary.id, which identifies the summary row itself.
  const vehicleId = assessRows.find((r) => r.vehicle_id)?.vehicle_id;

  return {
    info: {
      // Vehicle fields first so the ids below always win — /vehicle reports the
      // same id, but assess[].vehicle_id is the value proven against the list
      // and PUT /vehicle/{id}/.
      ...vehicleInfo(vehicle),
      id: vehicleId ?? vehicle?.id ?? claimSummary.id,
      ref_num: claimSummary.ref_num || vehicle?.ref_num,
      source_name: claimSummary.source_name || vehicle?.source_name,
    },

    summary: {
      parts_identified: claimSummary.parts_identified,
      // Only panels that still carry damage. A reviewer's correction to "No
      // Damage" arrives as a history row and belongs in assessment_details —
      // it is a finding about the panel — but it must not be counted as one.
      damaged_parts: damagedDetails.length,
      damaged_parts_list: damagedDetails.map((p) => p.part_name).filter(Boolean),
      severe_damage: buckets.severe,
      moderate_damage: buckets.moderate,
      minor_damage: buckets.minor,
      uploaded: claimSummary.uploaded,
      roi: claimSummary.roi,
    },

    assessment_details: assessmentDetails,

    all_damages: damages.map((d) => ({
      id: d.id,
      side: normalizeSide(d.side),
      picture: d.picture,
      file_name: d.file_name,
      dam_type: d.dam_type,
      coordinates: d.coordinates,
    })),

    uploads: carImages.map((img) => ({
      id: img.id,
      name: img.name,
      side: normalizeSide(img.side),
      picture: img.picture,
      upload_picture: img.picture,
    })),

    // v2 exposes only the ROI set, already mapped to uploads above. Kept empty
    // rather than duplicated, so the Images tab does not list each image twice.
    roi_images: [],

    // No warnings in this contract.
    warnings: {},

    assess_history: values(raw.assess_history),

    // Kept so nothing is silently lost if a caller needs a field not mapped here.
    _v2: raw,
  };
};

export default toLegacyAssessment;
